import {useEffect, useRef, useState} from 'react'
import {Modal, Button, Form, Row, Col, Spinner, Nav} from 'react-bootstrap'
import Select from 'react-select'
import {PolicyFilters, PolicySavedFilter} from '../policiesTypes'
import {DateInput} from 'app/tfi/components/DateInput'
import { MultiSelectWithActions } from 'app/tfi/components/MultiSelectWithActions'
import {parseId} from '../../../helpers'
import {INITIAL_POLICY_FILTERS, GENDER_OPTIONS, METAL_LEVELS} from '../../../helpers/consts'

interface ModalFiltersProps {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: any) => void
  setFilters: React.Dispatch<React.SetStateAction<PolicyFilters>>
  filters: PolicyFilters
  savedFilters?: PolicySavedFilter[]
  onSaveFilter?: (name: string, filters: PolicyFilters) => Promise<PolicySavedFilter | null>
  onUpdateFilter?: (id: string, name: string, filters: PolicyFilters) => Promise<PolicySavedFilter | null>
  onDeleteFilter?: (id: string) => Promise<boolean>
  users: any[]
  agents: any[]
  states: any[]
  immigrationStatuses: any[]
  signatureForms: any[]
  carriers: any[]
  policyStatuses: any[]
  documentStatuses: any[]
  paymentStatuses: any[]
  leadSources: any[]
  policyTypes: any[]
  isLoading?: boolean
  isAgent?: boolean
}

export const ModalFilters: React.FC<ModalFiltersProps> = ({
  isOpen,
  onClose,
  onApply,
  users,
  agents,
  states,
  immigrationStatuses,
  signatureForms,
  carriers,
  policyStatuses,
  documentStatuses,
  paymentStatuses,
  leadSources,
  policyTypes,
  setFilters,
  filters,
  savedFilters = [],
  onSaveFilter,
  onDeleteFilter,
  isLoading = false,
  isAgent = false,
}) => {
  const [activeTab, setActiveTab] = useState<'filters' | 'saved'>('filters')
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null)
  const [savedFilterName, setSavedFilterName] = useState('')
  const [shouldFocusSavedName, setShouldFocusSavedName] = useState(false)
  const [savedTabMode, setSavedTabMode] = useState<'save' | 'manage'>('manage')
  const savedNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (!selectedSavedFilterId) {
      setSavedFilterName('')
    }
  }, [isOpen, selectedSavedFilterId])

  useEffect(() => {
    if (activeTab !== 'saved' || !shouldFocusSavedName) return
    if (savedNameInputRef.current) {
      savedNameInputRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
      savedNameInputRef.current.focus()
      savedNameInputRef.current.select()
      setShouldFocusSavedName(false)
    }
  }, [activeTab, shouldFocusSavedName])

  const goToSavedTab = (mode: 'save' | 'manage') => {
    setActiveTab('saved')
    setSavedTabMode(mode)
    setShouldFocusSavedName(mode === 'save')
  }

  const handleSelectChange = (field: string, selected: any) => {
    console.log('field:', field)
    console.log('value:', selected)
    const isArray = Array.isArray(selected)
    let value: any = selected

    if (isArray) {
      value = selected.map((opt: any) => opt?.value).filter((v: any) => v !== '')
    } else {
      value = selected?.value
    }

    // Solo campos booleanos (ajusta esta lista según tus filtros booleanos)
    const booleanFields = [
      'isImportant',
      'isArchived',
      'isPolicyHolder',
      'withoutPolicy',
      'withoutLead',
      'myContacts',
      'hasMemberId',
      'commissionPayable',
      'advisorPaid',
      'advisorChargeback',
      'unassigned',
      'ownsCar',
      'ownsHome',
      'hasAutoPolicy',
      'hasHomePolicy',
      'carrierIsAgeIn',
      'carrierIsDelinquent',
      'carrierIsUnpaidPending',
      'carrierIsUnpaidPastDue',
    ]

    if (!isArray) {
      if (booleanFields.includes(field)) {
        if (value === 'true') value = true
        else if (value === 'false') value = false
        else if (value === '') value = undefined
      } else if (value === '') {
        value = undefined
      }
    }

    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
    console.log('filters:', filters)
  }

  const handleInputChange = (field: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleImmigrationStatusChange = (selected: any) => {
    const values = Array.isArray(selected)
      ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
      : selected?.value
        ? [String(selected.value).trim()].filter(Boolean)
        : []

    setFilters((prev: any) => ({
      ...prev,
      immigrationStatus: values,
    }))
  }

  const handleSubmit = () => {
    const parsedFilters = {
      ...filters,
      ...(filters.signatureForm && {signatureForm: filters.signatureForm}),
      ...(filters.policyType && {policyType: filters.policyType}),
      ...(filters.leadSource && {leadSource: filters.leadSource}),
      ...(filters.user && {user: filters.user}),
      ...(filters.carrier && {carrier: filters.carrier}), // array ok
      ...(filters.assignedTo && {assignedTo: filters.assignedTo}), // array ok
      ...(filters.stateEU && {stateEU: filters.stateEU}), // array ok
      ...(filters.stateJurisdictionType && {stateJurisdictionType: filters.stateJurisdictionType}),
      ...(filters.status && {status: filters.status}), // array ok
      ...(filters.immigrationStatus && {immigrationStatus: filters.immigrationStatus}),
      ...(filters.holderGender && {holderGender: filters.holderGender}),
      ...(filters.documentStatus && {documentStatus: filters.documentStatus}),
      ...(filters.paymentStatus && {paymentStatus: filters.paymentStatus}),
      ...(filters.metalLevel && {metalLevel: filters.metalLevel}),
      ...(filters.advisorPaid !== undefined && {advisorPaid: filters.advisorPaid}),
      ...(filters.advisorChargeback !== undefined && {advisorChargeback: filters.advisorChargeback}),
      ...(filters.unassigned !== undefined && {unassigned: filters.unassigned}),
      ...(filters.ownsCar !== undefined && {ownsCar: filters.ownsCar}),
      ...(filters.ownsHome !== undefined && {ownsHome: filters.ownsHome}),
      ...(filters.hasAutoPolicy !== undefined && {hasAutoPolicy: filters.hasAutoPolicy}),
      ...(filters.hasHomePolicy !== undefined && {hasHomePolicy: filters.hasHomePolicy}),
      ...(filters.paymentStatusDateAfter && {paymentStatusDateAfter: filters.paymentStatusDateAfter}),
      ...(filters.paymentStatusDateBefore && {paymentStatusDateBefore: filters.paymentStatusDateBefore}),
      ...(filters.documentsDueDateAfter && {documentsDueDateAfter: filters.documentsDueDateAfter}),
      ...(filters.documentsDueDateBefore && {documentsDueDateBefore: filters.documentsDueDateBefore}),
      ...(filters.advisorPaidDateAfter && {advisorPaidDateAfter: filters.advisorPaidDateAfter}),
      ...(filters.advisorPaidDateBefore && {advisorPaidDateBefore: filters.advisorPaidDateBefore}),
      ...(filters.advisorChargebackDateAfter && {advisorChargebackDateAfter: filters.advisorChargebackDateAfter}),
      ...(filters.advisorChargebackDateBefore && {advisorChargebackDateBefore: filters.advisorChargebackDateBefore}),
    }
    console.log('🚀 Filtros aplicados (parseados) desde el modal:', parsedFilters)
    onApply(parsedFilters)
    onClose()
  }

  const toArray = (value: unknown) => {
    if (Array.isArray(value)) return value
    if (typeof value === 'string') return value !== '' ? [value] : []
    return value != null ? [value] : []
  }

  const agentsOptions = [
    ...agents.map((u) => ({
      value: parseId(u.id),
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })),
  ]

  const userValues = toArray(filters.user).map(String)
  const selectedAgentsOptions = userValues.length
    ? agentsOptions.filter((opt) => userValues.includes(String(opt.value)))
    : null

  const assignedToOptions = [
    ...users.map((u) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })),
  ]

  const assignedToValues = toArray(filters.assignedTo).map(String)
  const selectedAsignedToOptions = assignedToValues.length
    ? assignedToOptions.filter((opt) => assignedToValues.includes(String(opt.value)))
    : null

  const stateEUOptions = [
    ...states.map((s) => ({
      value: s.code,
      label: `${s.name} (${s.code})`,
    })),
  ]

  const stateEUValues = toArray(filters.stateEU).map(String)
  const selectedStateEUOptions = stateEUValues.length
    ? stateEUOptions.filter((opt) => stateEUValues.includes(String(opt.value)))
    : null

  const carrierOptions = [
    ...carriers.map((s) => ({
      value: s.id,
      label: s.name,
    })),
  ]

  const carrierValues = toArray(filters.carrier).map(String)
  const selectedCarrierOptions = carrierValues.length
    ? carrierOptions.filter((opt) => carrierValues.includes(String(opt.value)))
    : null

  // Group policy statuses by category
  const policyStatusGrouped: {[key: string]: any[]} = {}
  policyStatuses.forEach((s) => {
    const category = s.category || 'OTHER'
    if (!policyStatusGrouped[category]) {
      policyStatusGrouped[category] = []
    }
    policyStatusGrouped[category].push({
      value: String(s.code),
      label: s.title,
    })
  })

  const policyStatusOptions = Object.keys(policyStatusGrouped).map(category => ({
    label: category.replace(/_/g, ' '),
    options: policyStatusGrouped[category]
  }))

  // Flatten for selection matching
  const allPolicyStatusOptions = policyStatusOptions.flatMap(cat => cat.options || [])

  const statusValues = toArray(filters.status).map(String)
  const selectedPolicyStatusOptions = statusValues.length
    ? allPolicyStatusOptions.filter((opt) => statusValues.includes(String(opt.value)))
    : null

  const documentStatusOptions = [
    ...documentStatuses.map((s) => ({
      value: String(s.value),
      label: s.label,
    })),
  ]

  const documentStatusValues = toArray(filters.documentStatus).map(String)
  const selectedDocumentStatusOptions = documentStatusValues.length
    ? documentStatusOptions.filter((opt) => documentStatusValues.includes(String(opt.value)))
    : null

  const payentStatusOptions = [
    ...paymentStatuses.map((s) => ({
      value: String(s.value),
      label: s.label,
    })),
  ]

  const paymentStatusValues = toArray(filters.paymentStatus).map(String)
  const selectedPaymentStatusOptions = paymentStatusValues.length
    ? payentStatusOptions.filter((opt) => paymentStatusValues.includes(String(opt.value)))
    : null

  const yesNoOptions = [
    {value: '', label: 'All policies'},
    {value: 'true', label: 'Yes'},
    {value: 'false', label: 'No'},
  ]

  const advisorPaidOptions = [
    {value: '', label: 'All policies'},
    {value: 'true', label: 'Paid'},
    {value: 'false', label: 'Not paid'},
  ]

  const selectedAdvisorPaid =
    filters.advisorPaid === true
      ? advisorPaidOptions[1]
      : filters.advisorPaid === false
      ? advisorPaidOptions[2]
      : null

  const advisorChargebackOptions = [
    {value: '', label: 'All policies'},
    {value: 'true', label: 'Chargeback'},
    {value: 'false', label: 'No chargeback'},
  ]

  const selectedAdvisorChargeback =
    filters.advisorChargeback === true
      ? advisorChargebackOptions[1]
      : filters.advisorChargeback === false
      ? advisorChargebackOptions[2]
      : null

  const selectedOwnsCar =
    filters.ownsCar === true ? yesNoOptions[1] : filters.ownsCar === false ? yesNoOptions[2] : null

  const selectedOwnsHome =
    filters.ownsHome === true ? yesNoOptions[1] : filters.ownsHome === false ? yesNoOptions[2] : null

  const selectedHasAutoPolicy =
    filters.hasAutoPolicy === true
      ? yesNoOptions[1]
      : filters.hasAutoPolicy === false
      ? yesNoOptions[2]
      : null

  const selectedHasHomePolicy =
    filters.hasHomePolicy === true
      ? yesNoOptions[1]
      : filters.hasHomePolicy === false
      ? yesNoOptions[2]
      : null

  // === POLICY TYPES (agrupados + planos para AppliedFiltersList) ===
const policyTypesOptions =
  policyTypes.map((cat) => ({
    label: cat.name,
    options:
      cat.policyTypeSet?.edges?.map(({ node }: any) => ({
        value: parseId(node.id),
        label: node.typePolicy,
        category: cat.name,
      })) || [],
  })) || []

// Versión plana para filtrar o mostrar labels legibles
const allPolicyTypeOptions =
  policyTypesOptions.flatMap((cat) =>
    (cat.options || []).map((opt) => ({
      ...opt,
      label: `${cat.label} › ${opt.label}`,
    }))
  )

  const policyTypeValues = toArray(filters.policyType).map(String)
  const selectedPolicyTypesOptions = policyTypeValues.length
    ? allPolicyTypeOptions.filter((opt) => policyTypeValues.includes(String(opt.value)))
    : null

  const leadSourcesOptions = [
    ...leadSources.map((s) => ({
      value: parseId(s.id),
      label: s.name,
    })),
  ]

  const leadSourceValues = toArray(filters.leadSource).map(String)
  const selecteLeadSourcesOptions = leadSourceValues.length
    ? leadSourcesOptions.filter((opt) => leadSourceValues.includes(String(opt.value)))
    : null
  

  const signatureStatusOptions = [
    {value: 'Signed', label: 'Signed'},
    {value: 'Not Signed', label: 'Not Signed'},
  ]

  const immigrationStatusOptions = [
    ...immigrationStatuses.map((s) => ({
      value: String(s.value),
      label: s.label,
    })),
  ]

  const immigrationStatusValues = toArray(filters.immigrationStatus).map(String)
  const selectedImmigrationStatus = immigrationStatusValues.length
    ? immigrationStatusOptions.filter((opt) => immigrationStatusValues.includes(String(opt.value)))
    : null

  const selectedSignatureStatus =
    signatureStatusOptions.find((opt) => opt.value === filters.signatureStatus) || null

  const signatureFormOptions = [
    ...signatureForms.map((s) => ({
      value: String(s.id),
      label: s.name,
    })),
  ]

  const signatureFormValues = toArray(filters.signatureForm).map(String)
  const selectedSignatureForm = signatureFormValues.length
    ? signatureFormOptions.filter((opt) => signatureFormValues.includes(String(opt.value)))
    : null

  // Agent of Record options (agents with NPN)
  const agentOfRecordOptions = agents
    .filter((a) => a && a.id)
    .map((a) => ({
      value: String(parseId(a.id)),
      label: `${a.firstName} ${a.lastName} - ${a.email} ${a.npn ? ` (${a.npn})` : ''}`,
    }))

  const agentOfRecordValues = toArray(filters.agentOfRecord).map(String)
  const selectedAgentOfRecord = agentOfRecordValues.length
    ? agentOfRecordOptions.filter((opt) => agentOfRecordValues.includes(String(opt.value)))
    : null

  const metalLevelOptions = METAL_LEVELS.map((level) => ({
    value: level.value,
    label: level.label,
  }))

  const selectedMetalLevel = filters.metalLevel
    ? metalLevelOptions.find((opt) => opt.value === filters.metalLevel)
    : null

  const savedFilterOptions = savedFilters.map((filter) => ({
    value: filter.id,
    label: filter.name,
  }))

  const selectedSavedFilterOption =
    savedFilterOptions.find((opt) => opt.value === selectedSavedFilterId) || null

  const handleSavedFilterSelect = (selected: any) => {
    if (!selected) {
      setSelectedSavedFilterId(null)
      setSavedFilterName('')
      return
    }

    const match = savedFilters.find((filter) => filter.id === selected.value)
    if (!match) return

    setSelectedSavedFilterId(match.id)
    setSavedFilterName(match.name)
    setFilters(() => ({...match.filters}))
  }

  const handleSaveAndApply = async () => {
    if (onSaveFilter) {
      const saved = await onSaveFilter(savedFilterName, filters)
      if (!saved) return
      setSelectedSavedFilterId(saved.id)
      setSavedFilterName(saved.name)
    }
    handleSubmit()
  }

  const handleDeleteFilter = async () => {
    if (!onDeleteFilter || !selectedSavedFilterId) return
    const confirmed = window.confirm('Delete this saved filter?')
    if (!confirmed) return
    const deleted = await onDeleteFilter(selectedSavedFilterId)
    if (!deleted) return
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
  }

  const handleClearFilters = () => {
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
    setFilters(() => ({...INITIAL_POLICY_FILTERS}))
  }

  const handleApplySavedFilter = () => {
    if (!selectedSavedFilterId) return
    onApply(filters)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' dialogClassName='policies-filters-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: '200px'}}>
            <Spinner animation='border' />
            <span className='ms-2'>Loading filter options...</span>
          </div>
        )}
        <div className='d-flex align-items-center justify-content-between mb-3 border-bottom pb-2'>
          <Nav
            variant='tabs'
            className='border-bottom-0'
            activeKey={activeTab}
            onSelect={(key) => {
              const nextTab = (key as 'filters' | 'saved') || 'filters'
              setActiveTab(nextTab)
              if (nextTab === 'saved') {
                setSavedTabMode('manage')
              }
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey='filters'>Filters</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='saved'>Saved filters</Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab === 'filters' ? (
            <div className='d-flex gap-2'>
              <Button variant='light' className='px-3' onClick={handleClearFilters}>
                Clear
              </Button>
              <Button
                variant='dark'
                className='px-3'
                onClick={() => goToSavedTab('save')}
              >
                <i className='bi bi-save me-1' /> Save Filter
              </Button>
              <Button variant='primary' className='px-3' onClick={handleSubmit}>
                <i className='bi bi-search me-1' /> Apply Filters
              </Button>
            </div>
          ) : null}
        </div>
        <Form style={{opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto'}}>
          {activeTab === 'saved' ? (
            <>
              <div className='mb-2'>
                <h5 className='mb-1'>Manage saved filters</h5>
                {savedTabMode === 'manage' ? (
                  <div className='text-muted'>Select a saved filter for quick access to data</div>
                ) : null}
              </div>
              {savedTabMode === 'save' ? (
                <Row className='mb-3 align-items-end'>
                  <Col md={8}>
                    <Form.Label>Filter name</Form.Label>
                    <Form.Control
                      ref={savedNameInputRef}
                      value={savedFilterName}
                      placeholder='Enter filter name'
                      onChange={(e) => setSavedFilterName(e.target.value)}
                    />
                    <div className='text-muted mt-2'>
                      Save the current filters so you can reuse them later.
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Label>&nbsp;</Form.Label>
                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleSaveAndApply}
                        disabled={!savedFilterName.trim()}
                      >
                        Save and apply
                      </Button>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row className='mb-2 align-items-center'>
                  <Col md={8}>
                    <Form.Label>Saved Filters</Form.Label>
                    <Select
                      value={selectedSavedFilterOption}
                      options={savedFilterOptions}
                      onChange={handleSavedFilterSelect}
                      isClearable
                      placeholder='Select saved filter'
                    />
                  </Col>
                  <Col md={4}>
                    <div className='d-flex flex-wrap justify-content-end gap-2'>
                      <Button variant='light' className='px-3' onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        variant='outline-danger'
                        className='px-3'
                        onClick={handleDeleteFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        Delete
                      </Button>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleApplySavedFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        Apply saved filter
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
              <hr />
            </>
          ) : null}
          {activeTab === 'filters' ? (
            <>
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Agent</Form.Label>
              <MultiSelectWithActions
                value={selectedAgentsOptions}
                options={agentsOptions}
                onChange={(selected) => handleSelectChange('user', selected)}
              />
            </Col>
            <Col>
              <Form.Label>Assigned To</Form.Label>
              <MultiSelectWithActions
                value={selectedAsignedToOptions}
                options={assignedToOptions}
                onChange={(selected) => handleSelectChange('assignedTo', selected)}
              />
            </Col>
            <Col>
              <Form.Label>Carrier</Form.Label>
              <MultiSelectWithActions
                value={selectedCarrierOptions}
                options={carrierOptions}
                onChange={(selected) => handleSelectChange('carrier', selected)}
              />
            </Col>
            <Col>
              <Form.Label>State</Form.Label>
              <MultiSelectWithActions
                value={selectedStateEUOptions}
                options={stateEUOptions}
                onChange={(selected) => handleSelectChange('stateEU', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>State Jurisdiction Type</Form.Label>
              <Select
                value={
                  filters.stateJurisdictionType === 'FEDERAL'
                    ? {value: 'FEDERAL', label: 'Federal'}
                    : filters.stateJurisdictionType === 'STATE'
                    ? {value: 'STATE', label: 'State'}
                    : null
                }
                options={[
                  {value: '', label: 'All states'},
                  {value: 'FEDERAL', label: 'Federal'},
                  {value: 'STATE', label: 'State'},
                ]}
                onChange={(selected) => handleSelectChange('stateJurisdictionType', selected)}
                isClearable
                placeholder="Select jurisdiction type"
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                value={filters.zipCode || ''}
                type='number'
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
              />
            </Col>
            <Col>
              <Form.Label>Immigration Status</Form.Label>
              <MultiSelectWithActions
                value={selectedImmigrationStatus}
                options={immigrationStatusOptions}
                onChange={handleImmigrationStatusChange}
              />
            </Col>
            <Col>
              <Form.Label>Policy Status</Form.Label>
              <MultiSelectWithActions
                value={selectedPolicyStatusOptions}
                options={policyStatusOptions}
                onChange={(selected) => handleSelectChange('status', selected)}
              />
            </Col>
            <Col>
              <Form.Label>Document Status</Form.Label>
              <MultiSelectWithActions
                value={selectedDocumentStatusOptions}
                options={documentStatusOptions}
                onChange={(selected) => handleSelectChange('documentStatus', selected)}
              />
            </Col>
            <Col>
              <Form.Label>Payment status</Form.Label>
              <MultiSelectWithActions
                value={selectedPaymentStatusOptions}
                options={payentStatusOptions}
                onChange={(selected) => handleSelectChange('paymentStatus', selected)}
              />
            </Col>
            <Col>
              <Form.Label>Policy Types</Form.Label>
              <MultiSelectWithActions
                value={selectedPolicyTypesOptions}
                options={policyTypesOptions}
                onChange={(selected) => handleSelectChange('policyType', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Source</Form.Label>
              <MultiSelectWithActions
                value={selecteLeadSourcesOptions}
                options={leadSourcesOptions}
                onChange={(selected) => handleSelectChange('leadSource', selected)}
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Metal Level</Form.Label>
              <Select
                value={selectedMetalLevel}
                options={metalLevelOptions}
                onChange={(selected) => handleSelectChange('metalLevel', selected)}
                isClearable
                placeholder="Select Metal Level"
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Gender (Holder)</Form.Label>
              <Select
                value={
                  filters.holderGender
                    ? GENDER_OPTIONS.find((opt) => opt.value === filters.holderGender) || null
                    : null
                }
                options={GENDER_OPTIONS}
                onChange={(selected) => handleSelectChange('holderGender', selected)}
                isClearable
                placeholder='Select...'
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Member ID Status</Form.Label>
              <Select
                value={
                  filters.hasMemberId === true
                    ? {value: 'true', label: 'With Member ID'}
                    : filters.hasMemberId === false
                    ? {value: 'false', label: 'Without Member ID'}
                    : null
                }
                options={[
                  {value: '', label: 'All policies'},
                  {value: 'true', label: 'With Member ID'},
                  {value: 'false', label: 'Without Member ID'},
                ]}
                onChange={(selected) => handleSelectChange('hasMemberId', selected)}
                isClearable
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Commission Report Status</Form.Label>
              <Select
                value={
                  filters.commissionPayable === true
                    ? {value: 'true', label: 'Considered for commission'}
                    : filters.commissionPayable === false
                    ? {value: 'false', label: 'Not considered for commission'}
                    : null
                }
                options={[
                  {value: '', label: 'All policies'},
                  {value: 'true', label: 'Considered for commission'},
                  {value: 'false', label: 'Not considered for commission'},
                ]}
                onChange={(selected) => handleSelectChange('commissionPayable', selected)}
                isClearable
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Unassigned Policies</Form.Label>
              <Select
                value={
                  filters.unassigned === true
                    ? {value: 'true', label: 'Only unassigned'}
                    : filters.unassigned === false
                    ? {value: 'false', label: 'Only assigned'}
                    : null
                }
                options={[
                  {value: '', label: 'All policies'},
                  {value: 'true', label: 'Only unassigned'},
                  {value: 'false', label: 'Only assigned'},
                ]}
                onChange={(selected) => handleSelectChange('unassigned', selected)}
                isClearable
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Owns car</Form.Label>
              <Select
                value={selectedOwnsCar}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('ownsCar', selected)}
                isClearable
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Owns home</Form.Label>
              <Select
                value={selectedOwnsHome}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('ownsHome', selected)}
                isClearable
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Has auto policy</Form.Label>
              <Select
                value={selectedHasAutoPolicy}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('hasAutoPolicy', selected)}
                isClearable
              />
            </Col>
            <Col xs={12} md={2} lg={2} xl={2}>
              <Form.Label>Has home policy</Form.Label>
              <Select
                value={selectedHasHomePolicy}
                options={yesNoOptions}
                onChange={(selected) => handleSelectChange('hasHomePolicy', selected)}
                isClearable
              />
            </Col>
          </Row>
          <hr />
          <Row className='mb-3 g-3'>
            <Col xs={12} md={6} xl>
              <DateInput
                id='effectiveDateAfter'
                name='effectiveDateAfter'
                label='Effective Date From'
                value={filters.effectiveDateAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='createdAfter'
                name='createdAfter'
                label='Created Date From'
                value={filters.createdAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='paymentStatusDateAfter'
                name='paymentStatusDateAfter'
                label='Payment Status Date From'
                value={filters.paymentStatusDateAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='canceledAfter'
                name='canceledAfter'
                label='Cancellation Date From'
                value={filters.canceledAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='documentsDueDateAfter'
                name='documentsDueDateAfter'
                label='Documents Due Date From'
                value={filters.documentsDueDateAfter}
                onChange={handleInputChange}
              />
            </Col>
          </Row>
          <Row className='mb-3 g-3'>
            <Col xs={12} md={6} xl>
              <DateInput
                id='effectiveDateBefore'
                name='effectiveDateBefore'
                label='Effective Date To'
                value={filters.effectiveDateBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='createdBefore'
                name='createdBefore'
                label='Created Date To'
                value={filters.createdBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='paymentStatusDateBefore'
                name='paymentStatusDateBefore'
                label='Payment Status Date To'
                value={filters.paymentStatusDateBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='canceledBefore'
                name='canceledBefore'
                label='Cancellation Date To'
                value={filters.canceledBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl>
              <DateInput
                id='documentsDueDateBefore'
                name='documentsDueDateBefore'
                label='Documents Due Date To'
                value={filters.documentsDueDateBefore}
                onChange={handleInputChange}
              />
            </Col>
          </Row>
          <hr />
          <h5>Signature Form Filters</h5>
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-2 row-cols-xl-3'>
            <Col>
              <Form.Label>Signature Form Status</Form.Label>
              <Select
                value={selectedSignatureStatus}
                options={signatureStatusOptions}
                onChange={(selected) => handleSelectChange('signatureStatus', selected)}
                isClearable
              />
            </Col>
            <Col>
              <Form.Label>Signature Form</Form.Label>
              <MultiSelectWithActions
                value={selectedSignatureForm}
                options={signatureFormOptions}
                onChange={(selected) => handleSelectChange('signatureForm', selected)}
              />
            </Col>
            <Col>
              <Form.Label>Agent NPN in Signature Form</Form.Label>
              <MultiSelectWithActions
                value={selectedAgentOfRecord}
                options={agentOfRecordOptions}
                onChange={(selected) => handleSelectChange('agentOfRecord', selected)}
              />
            </Col>
          </Row>
          <hr />
          <h5>Carrier Import Filters</h5>
          <Row className='mb-3 g-2 align-items-end'>
            <Col xs={12} md={6} xl={2}>
              <Form.Label>Carrier Status</Form.Label>
              <Select
                value={
                  filters.carrierStatusGeneric
                    ? (Array.isArray(filters.carrierStatusGeneric)
                        ? filters.carrierStatusGeneric
                        : [filters.carrierStatusGeneric]
                      ).map((v) => ({value: v, label: v.replace(/_/g, ' ')}))
                    : null
                }
                options={[
                  {value: 'IN_FORCE', label: 'In Force'},
                  {value: 'TERMINATED', label: 'Terminated'},
                  {value: 'GRACE_PERIOD', label: 'Grace Period'},
                  {value: 'PENDING_EFFECTUATION', label: 'Pending Effectuation'},
                  {value: 'PAST_DUE', label: 'Past Due'},
                  {value: 'DELINQUENT', label: 'Delinquent'},
                  {value: 'FUTURE_EFFECTIVE', label: 'Future Effective'},
                  {value: 'UNKNOWN', label: 'Unknown'},
                ]}
                onChange={(selected) => handleSelectChange('carrierStatusGeneric', selected)}
                isMulti
                isClearable
                placeholder='Select carrier status...'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateAfter'
                name='carrierLastAsOfDateAfter'
                label='Carrier As-of Date From'
                value={filters.carrierLastAsOfDateAfter}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateBefore'
                name='carrierLastAsOfDateBefore'
                label='Carrier As-of Date To'
                value={filters.carrierLastAsOfDateBefore}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'>Age-In</Form.Label>
              <Select
                value={
                  filters.carrierIsAgeIn === true ? {value: 'true', label: 'Yes'}
                  : filters.carrierIsAgeIn === false ? {value: 'false', label: 'No'}
                  : null
                }
                options={[
                  {value: 'true', label: 'Yes'},
                  {value: 'false', label: 'No'},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsAgeIn', selected)}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'>Delinquent</Form.Label>
              <Select
                value={
                  filters.carrierIsDelinquent === true ? {value: 'true', label: 'Yes'}
                  : filters.carrierIsDelinquent === false ? {value: 'false', label: 'No'}
                  : null
                }
                options={[
                  {value: 'true', label: 'Yes'},
                  {value: 'false', label: 'No'},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsDelinquent', selected)}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'>Unpaid Pending</Form.Label>
              <Select
                value={
                  filters.carrierIsUnpaidPending === true ? {value: 'true', label: 'Yes'}
                  : filters.carrierIsUnpaidPending === false ? {value: 'false', label: 'No'}
                  : null
                }
                options={[
                  {value: 'true', label: 'Yes'},
                  {value: 'false', label: 'No'},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsUnpaidPending', selected)}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'>Unpaid Past Due</Form.Label>
              <Select
                value={
                  filters.carrierIsUnpaidPastDue === true ? {value: 'true', label: 'Yes'}
                  : filters.carrierIsUnpaidPastDue === false ? {value: 'false', label: 'No'}
                  : null
                }
                options={[
                  {value: 'true', label: 'Yes'},
                  {value: 'false', label: 'No'},
                ]}
                onChange={(selected) => handleSelectChange('carrierIsUnpaidPastDue', selected)}
                isClearable
                placeholder='Any'
              />
            </Col>
          </Row>

          {!isAgent && (
            <>
              <hr />
              <h5>Advisor Payment Filters</h5>
              <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
                <Col>
                  <Form.Label>Advisor Payment Status</Form.Label>
                  <Select
                    value={selectedAdvisorPaid}
                    options={advisorPaidOptions}
                    onChange={(selected) => handleSelectChange('advisorPaid', selected)}
                    isClearable
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorPaidDateAfter'
                    name='advisorPaidDateAfter'
                    label='Advisor Paid Date From'
                    value={filters.advisorPaidDateAfter}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorPaidDateBefore'
                    name='advisorPaidDateBefore'
                    label='Advisor Paid Date To'
                    value={filters.advisorPaidDateBefore}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
              <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
                <Col>
                  <Form.Label>Chargeback Status</Form.Label>
                  <Select
                    value={selectedAdvisorChargeback}
                    options={advisorChargebackOptions}
                    onChange={(selected) => handleSelectChange('advisorChargeback', selected)}
                    isClearable
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorChargebackDateAfter'
                    name='advisorChargebackDateAfter'
                    label='Chargeback Date From'
                    value={filters.advisorChargebackDateAfter}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col>
                  <DateInput
                    id='advisorChargebackDateBefore'
                    name='advisorChargebackDateBefore'
                    label='Chargeback Date To'
                    value={filters.advisorChargebackDateBefore}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </>
          )}
            </>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {activeTab === 'filters' ? (
          <>
            <Button variant='light' className='px-4' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='dark' className='px-4' onClick={handleSubmit}>
              <i className='bi bi-search me-1' /> Apply Filters
            </Button>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  )
}
